<template>
  <div>
    <dialog-confirm
      v-if="context === 'update'"
      ref="update-title-dialog"
      :confirm-text="$t('yes')"
      :reject-text="$t('no')">
      <template #title>
        {{ $t('reports_ui.update_report_title_query') }}
      </template>
      <div class="text-subtitle-1">
        {{ $t('reports_ui.report_parameters_title_change_text')}}
      </div>
      <h5 class="mt-2">
        {{ defaultReportName() }}
      </h5>
    </dialog-confirm>
    <v-form>
      <v-text-field
        v-if="context !== 'create'"
        v-model="localReport.name"
        :label="$tc('name')"
        prepend-inner-icon="mdi-form-textbox"/>
      <v-select
        :label="$tc('product')"
        v-model="selectedProductId"
        :items="productOptions"
        :error-messages="selectErrors"/>
      <div class="d-flex align-center">
        <date-picker
          v-model="startDate"
          :timezone="timezone"
          :output-moment="true"
          :label="$t('start_date')"
          prepend-inner-icon="mdi-calendar"
          :error-messages="dateRangeErrors"
          :reset-on-timezone-change="context === 'create'"/>
        <span class="text-subtitle-1 mx-2">{{ $t('to') }}</span>
        <date-picker
          v-model="endDate"
          :timezone="timezone"
          :output-moment="true"
          :end-of-day="true"
          :label="$t('end_date')"
          prepend-inner-icon="mdi-calendar"
          :error="dateRangeErrors.length > 0"
          :reset-on-timezone-change="context === 'create'"/>
      </div>
      <date-selector-shortcuts :lowerBound.sync="startDate" :upperBound.sync="endDate" :timezone="timezone"/>
    </v-form>
  </div>
</template>
<script>

import DatePicker from '@/components/shared/DatePicker'
import DateSelectorShortcuts from '@/components/report/DateSelectorShortcuts'
import DialogConfirm from '@/components/shared/DialogConfirm'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ReportFormMixin from '@/components/report/ReportFormMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import ReportUtils from '@/components/report/ReportUtils'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { get, map, find } from 'lodash'

export default {
  name: 'StartTemperaturesReportForm',
  components: {
    DatePicker,
    DateSelectorShortcuts,
    DialogConfirm
  },
  mixins: [
    CurrentUserMixin,
    ReportFormMixin,
    ValidationMixin
  ],
  validations: {
    localReport: {
      name: {
        required: requiredIf(function () {
          return this.action !== 'create'
        })
      }
    },
    selectedProductId: {
      required
    },
    dateRange: {
      rangeIsValid: function () {
        return ReportUtils.dateRangeIsValid(this.startDate, this.endDate)
      }
    }
  },
  data () {
    return {
      reportType: 'StartTemperaturesReport',
      selectedProductId: null,
      availableProducts: {},
      startDate: null,
      endDate: null
    }
  },
  methods: {
    populateReportForm () {
      this.fetchProducts()
      this.preSelectProduct()
      this.preSelectDates()
    },
    /**
     * Using a previous report, find all selected violations and set the state
     * of this component to match, default to selecting all violations.
     *
     * @var ReportRecord report
     * @return undefined
     */
    preSelectProduct () {
      const preSelectedProductId = get(this, 'report.parameters.product_id')
      if (preSelectedProductId) {
        this.selectedProductId = preSelectedProductId
      }
    },
    /**
     * Fetch all known violation types from the back-end and update this form
     * with those results. When results are received update the form state to
     * pre-select all violations or all violations on a previous report if using
     * a previous report as a template.
     *
     * @var ReportRecord report
     * @return undefined
     */
    fetchProducts () {
      this.$orm('Product')
        .findAll({})
        .then((collection) => {
          this.availableProducts = collection.getItems()
          this.preSelectProduct()
        })
        .catch(error => {
          this.handleError(error)
        })
    },
    /**
     * Override mixin method setLocalParameters, so we can prompt and update the report name when the report parameters
     * change.
     * @param reportParameters
     * @returns {Promise<void>}
     */
    async setLocalReportParameters (reportParameters) {
      if (this.action === 'create' || this.action === 'duplicate') {
        this.localReport = this.$orm('Report').createRecord({
          type: this.reportType,
          name: this.localReport.name || this.defaultReportName(),
          parameters: reportParameters
        })
      } else {
        const changed =
          reportParameters.startDate !== get(this.report, 'parameters.startDate') ||
          reportParameters.endDate !== get(this.report, 'parameters.endDate') ||
          reportParameters.product_id !== get(this.report, 'parameters.product_id')

        if (changed && await this.$refs['update-title-dialog'].confirm()) {
          this.localReport.name = this.defaultReportName()
        }
        this.localReport.parameters = reportParameters
      }
    },
    /**
     * Implement ReportFormMixin method defaultReportName
     * @returns {string}
     */
    defaultReportName () {
      const startDateName = this.startDate.locale(this.$i18n.locale).format(this.$t('date_format_string'))
      const endDateName = this.endDate.locale(this.$i18n.locale).format(this.$t('date_format_string'))
      const selectedProduct = find(this.productOptions, x => x.value === this.selectedProductId, 0)
      const productName = get(selectedProduct, 'text', 'Product')
      return `${productName} ${this.$t('reports_ui.start_temperatures_report.name')} ${startDateName} - ${endDateName}`
    },
    /**
     * Implement ReportFormMixin method buildReportParameters
     * @returns {{}}
     */
    buildReportParameters () {
      const reportParameters = {}
      reportParameters.startDate = this.startDate.format()
      reportParameters.endDate = this.endDate.format()
      reportParameters.product_id = this.selectedProductId
      reportParameters.customer_id = get(this, 'localReport.parameters.customerId') || this.customerContext.id
      reportParameters.timezone = this.timezone
      return reportParameters
    }
  },
  computed: {
    /**
     * Returns an array of available products formatted for use in the v-select component
     * @return array
     */
    productOptions () {
      return map(this.availableProducts, (product) => {
        return {
          text: product.name,
          value: product.id
        }
      })
    },
    /**
     * Validation errors for the date range picker
     * @returns {Array}
     */
    dateRangeErrors () {
      return this.validationErrors([{
        prop: this.$v.dateRange,
        message: this.$t('invalid_date_range')
      }])
    },
    /**
     * Validation errors for the product selector
     * @returns {Array}
     */
    selectErrors () {
      return this.validationErrors([{
        prop: this.$v.selectedProductId,
        message: this.$t('required')
      }])
    }
  }
}

</script>
